/**
 * TokenStore - In-memory storage for JWT access and refresh tokens
 *
 * Stores tokens from PKCE callback/refresh. Used by authenticatedFetch
 * to add Bearer header to API requests.
 *
 * Access token: in-memory only (never persisted).
 * Refresh token: persisted in sessionStorage (all envs) for page-refresh continuity.
 */

import { z } from 'zod';

const REFRESH_THRESHOLD_SECONDS = 5 * 60; // 5 minutes before expiry
const REFRESH_COOLDOWN_MS = 30_000; // Don't refresh again within 30s of last successful refresh
const REFRESH_TOKEN_STORAGE_KEY = 'periverse_refresh_token';
const AUTOLOGOUT_TRACE_PREFIX = '[AUTOLOGOUT-TRACE][token-store]';

function logAutologoutTrace(
  message: string,
  details?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') {
    return;
  }

  console.log(AUTOLOGOUT_TRACE_PREFIX, message, details ?? {});
}

/** Stored in sessionStorage - refresh token only (access token never persisted) */
const StoredRefreshTokenSchema = z.object({
  refreshToken: z.string().min(1),
  expiresAt: z.number().optional(),
});
type StoredRefreshToken = z.infer<typeof StoredRefreshTokenSchema>;

export type RefreshTokenFn = () => Promise<string | null>;

class TokenStoreImpl {
  private accessToken: string | null = null;
  private idToken: string | null = null;
  private refreshToken: string | null = null;
  private expiresAt: number | null = null;
  private refreshFn: RefreshTokenFn | null = null;
  private refreshPromise: Promise<string | null> | null = null;
  private lastRefreshAt = 0;
  private onRefreshSuccess: (() => void) | null = null;

  /**
   * Store tokens from callback or refresh response.
   * Access token: memory only. Refresh token: persisted to sessionStorage for page refresh.
   * Id token: memory only, used for session derivation (email, name, etc.).
   */
  setTokens(
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
    idToken?: string | null
  ): void {
    this.accessToken = accessToken;
    this.idToken = idToken ?? null;
    this.refreshToken = refreshToken;
    this.expiresAt = Math.floor(Date.now() / 1000) + expiresIn;
    logAutologoutTrace('tokens:set', {
      expiresIn,
      expiresAt: this.expiresAt,
      hasIdToken: Boolean(idToken),
    });

    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(
          REFRESH_TOKEN_STORAGE_KEY,
          JSON.stringify({
            refreshToken,
            expiresAt: this.expiresAt,
          } satisfies StoredRefreshToken)
        );
      } catch {
        // Ignore storage errors
      }
    }
  }

  /**
   * Get current access token (or null if none/expired)
   */
  getAccessToken(): string | null {
    this.maybeRestoreFromSessionStorage();
    if (!this.accessToken || this.isExpired()) {
      return null;
    }
    return this.accessToken;
  }

  /**
   * Get id token (OIDC) for session derivation - email, name, etc.
   * In-memory only; received from refresh or callback.
   */
  getIdToken(): string | null {
    this.maybeRestoreFromSessionStorage();
    return this.idToken;
  }

  /**
   * Get refresh token for token refresh flow
   */
  getRefreshToken(): string | null {
    this.maybeRestoreFromSessionStorage();
    return this.refreshToken;
  }

  /**
   * Check if access token is expired
   */
  isExpired(): boolean {
    if (!this.expiresAt) return true;
    return Date.now() / 1000 >= this.expiresAt;
  }

  /**
   * Check if token should be refreshed proactively (within threshold)
   */
  shouldRefresh(): boolean {
    if (!this.expiresAt) return true;
    return this.expiresAt - Date.now() / 1000 < REFRESH_THRESHOLD_SECONDS;
  }

  /**
   * Clear all stored tokens (on logout)
   */
  clear(): void {
    this.accessToken = null;
    this.idToken = null;
    this.refreshToken = null;
    this.expiresAt = null;
    this.refreshPromise = null;
    this.lastRefreshAt = 0;
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      } catch {
        // Ignore
      }
    }
  }

  /**
   * Check if any tokens are stored
   */
  hasTokens(): boolean {
    this.maybeRestoreFromSessionStorage();
    return this.accessToken !== null && this.refreshToken !== null;
  }

  /**
   * Register the refresh function (called by app on init).
   * When refresh is needed (401), this fn is invoked to get new tokens.
   * Pass null to clear.
   */
  setRefreshFn(fn: RefreshTokenFn | null): void {
    this.refreshFn = fn;
  }

  /**
   * Register callback invoked when refresh succeeds.
   * Used to notify app (e.g. invalidate React Query cache) so data refetches with new token.
   * Pass null to clear.
   */
  setOnRefreshSuccess(fn: (() => void) | null): void {
    this.onRefreshSuccess = fn;
  }

  /**
   * Attempt to refresh tokens. Calls the registered refresh fn.
   * Returns new access token or null if refresh failed.
   * Deduplicates concurrent refresh calls - all wait on the same in-flight refresh.
   */
  async refresh(): Promise<string | null> {
    if (!this.refreshFn) return null;

    if (this.refreshPromise) {
      logAutologoutTrace('refresh:deduped', {
        lastRefreshAt: this.lastRefreshAt,
      });
      return this.refreshPromise;
    }

    logAutologoutTrace('refresh:start', {
      hasAccessToken: Boolean(this.accessToken),
      hasRefreshToken: Boolean(this.refreshToken),
      expiresAt: this.expiresAt,
      shouldRefresh: this.shouldRefresh(),
      isExpired: this.isExpired(),
    });
    this.refreshPromise = (async (): Promise<string | null> => {
      try {
        const token = await this.refreshFn!();
        if (token) {
          this.lastRefreshAt = Date.now();
          this.onRefreshSuccess?.();
          logAutologoutTrace('refresh:success', {
            lastRefreshAt: this.lastRefreshAt,
            expiresAt: this.expiresAt,
          });
        } else {
          logAutologoutTrace('refresh:null-token');
        }
        return token;
      } catch {
        logAutologoutTrace('refresh:error');
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Get access token, refreshing proactively if shouldRefresh()
   * Uses cooldown to avoid refresh storms when threshold > token lifetime (e.g. test scenario)
   * When we have refresh token but no valid access token (e.g. after page restore), always attempt refresh.
   */
  async getAccessTokenOrRefresh(): Promise<string | null> {
    this.maybeRestoreFromSessionStorage();

    // When we have refresh token but no valid access token (e.g. after page restore),
    // always attempt refresh - do not rely on shouldRefresh() which uses stored expiresAt
    const hasValidAccessToken = this.getAccessToken() !== null;
    if (!hasValidAccessToken && this.getRefreshToken() && this.refreshFn) {
      logAutologoutTrace('access:refresh-required-no-valid-token', {
        hasRefreshToken: Boolean(this.getRefreshToken()),
        expiresAt: this.expiresAt,
      });
      const newToken = await this.refresh();
      return newToken;
    }

    if (this.shouldRefresh() && this.refreshFn) {
      const withinCooldown =
        this.lastRefreshAt > 0 &&
        Date.now() - this.lastRefreshAt < REFRESH_COOLDOWN_MS;
      const hasValidToken = this.getAccessToken() !== null;
      if (withinCooldown && hasValidToken) {
        logAutologoutTrace('access:reuse-within-cooldown', {
          lastRefreshAt: this.lastRefreshAt,
          expiresAt: this.expiresAt,
        });
        return this.getAccessToken();
      }
      logAutologoutTrace('access:proactive-refresh', {
        expiresAt: this.expiresAt,
        nowSeconds: Date.now() / 1000,
        refreshThresholdSeconds: REFRESH_THRESHOLD_SECONDS,
      });
      const newToken = await this.refresh();
      return newToken;
    }
    logAutologoutTrace('access:reuse-current-token', {
      expiresAt: this.expiresAt,
    });
    return this.getAccessToken();
  }

  /**
   * Restore refresh token from sessionStorage (all envs) when memory is empty.
   * Only restores refreshToken; accessToken stays null so getAccessTokenOrRefresh triggers refresh.
   */
  private maybeRestoreFromSessionStorage(): void {
    if (
      typeof window !== 'undefined' &&
      !this.accessToken &&
      !this.refreshToken
    ) {
      try {
        const stored = sessionStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        if (stored) {
          const parsed = StoredRefreshTokenSchema.parse(JSON.parse(stored));
          this.refreshToken = parsed.refreshToken;
          this.expiresAt = parsed.expiresAt ?? null;
          // accessToken stays null - refresh will be triggered
        }
      } catch {
        // Ignore — invalid/missing refresh token, will trigger fresh refresh
      }
    }
  }
}

export const tokenStore = new TokenStoreImpl();
